import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, CartItemCustomization, MenuItem, Formula } from '../types';

// Simple UUID generator (no external dependency)
const generateId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

interface CartState {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (id: string) => void;
  updateItemQuantity: (id: string, quantity: number) => void;
  updateItemNotes: (id: string, notes: string) => void;
  clearCart: () => void;
  getItemCount: () => number;
  getSubtotal: () => number;
}

const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => {
        const newItem: CartItem = {
          ...item,
          id: generateId(),
        };
        set((state) => ({ items: [...state.items, newItem] }));
      },

      removeItem: (id) => {
        set((state) => ({ items: state.items.filter((item) => item.id !== id) }));
      },

      updateItemQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
          return;
        }
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id
              ? {
                  ...item,
                  quantity,
                  totalPrice: item.unitPrice * quantity,
                }
              : item
          ),
        }));
      },

      updateItemNotes: (id, notes) => {
        set((state) => ({
          items: state.items.map((item) => (item.id === id ? { ...item, notes } : item)),
        }));
      },

      clearCart: () => {
        set({ items: [] });
      },

      getItemCount: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      getSubtotal: () => {
        return get().items.reduce((sum, item) => sum + item.totalPrice, 0);
      },
    }),
    {
      name: 'ballm-cart',
    }
  )
);

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  addToCart: (
    item: MenuItem | Formula,
    quantity: number,
    customizations?: CartItemCustomization[],
    notes?: string
  ) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  updateNotes: (id: string, notes: string) => void;
  clearCart: () => void;
  isMenuItemInCart: (menuItemId: number) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const store = useCartStore();

  const addToCart = useCallback(
    (
      item: MenuItem | Formula,
      quantity: number,
      customizations: CartItemCustomization[] = [],
      notes?: string
    ) => {
      const isFormula = 'items' in item;
      const basePrice = parseFloat(item.price.toString());
      const extrasPrice = customizations.reduce(
        (sum, c) => sum + parseFloat(c.option.price_extra.toString()) * c.quantity,
        0
      );
      const unitPrice = basePrice + extrasPrice;
      const totalPrice = unitPrice * quantity;

      const cartItem: Omit<CartItem, 'id'> = {
        menuItem: isFormula ? undefined : (item as MenuItem),
        formula: isFormula ? (item as Formula) : undefined,
        quantity,
        customizations,
        notes,
        unitPrice,
        totalPrice,
      };

      store.addItem(cartItem);
    },
    [store]
  );

  const isMenuItemInCart = useCallback(
    (menuItemId: number): boolean => {
      return store.items.some((item) => item.menuItem?.id === menuItemId);
    },
    [store.items]
  );

  const value = useMemo<CartContextType>(
    () => ({
      items: store.items,
      itemCount: store.getItemCount(),
      subtotal: store.getSubtotal(),
      addToCart,
      removeFromCart: store.removeItem,
      updateQuantity: store.updateItemQuantity,
      updateNotes: store.updateItemNotes,
      clearCart: store.clearCart,
      isMenuItemInCart,
    }),
    [store, addToCart, isMenuItemInCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
